import { Roles, UserList } from '@common/types/user';
import { Flag } from '@components/display/images';
import { BorderlessTableCell, Table } from '@components/display/tables';
import { Button, CloseButton } from '@components/utility/buttons';
import { InputField } from '@components/utility/inputs';
import { createMailchimpAudienceDialogHeaders, getMemberOrderingFields } from '@members/constants';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Grid2,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    useCreateMailchimpAudienceByFilterMutation,
    useCreateMailchimpAudienceByIDsMutation,
    useGetAccountsByIDsQuery,
    useGetAccountsQuery,
} from '@services/accounts';
import { handleError, toaster } from '@utils/common';
import { getRoleName } from '@utils/user';
import { useRef, useState } from 'react';

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

export const CreateMailchimpAudienceDialog = ({
    user_ids,
    filters,
    search,
    ...props
}: DialogProps & { user_ids?: number[]; filters?: Record<string, string[]>; search?: string | null }) => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [ordering, setOrdering] = useState<string | undefined>(undefined);
    const footerPortalRef = useRef<HTMLDivElement>(null);
    const [audienceName, setAudienceName] = useState('');

    const {
        data: accountsByID,
        isLoading: isAccountsByIDLoading,
        isFetching: isAccountsByIDFetching,
    } = useGetAccountsByIDsQuery(
        { user_ids: user_ids as number[], page, page_size: rowsPerPage, ordering: getMemberOrderingFields(ordering) },
        { skip: !props.open || !user_ids || user_ids.length === 0 }
    );

    const {
        data: accounts,
        isLoading: areAccountsLoading,
        isFetching: areAccountsFetching,
    } = useGetAccountsQuery(
        {
            page,
            page_size: rowsPerPage,
            ordering: getMemberOrderingFields(ordering),
            search: search ?? '',
            role: filters?.role ? (filters.role as Roles[]) : undefined,
            sales_manager: filters?.sales_manager?.map(Number) ?? undefined,
            country: filters?.country ?? undefined,
        },
        { skip: !props.open || (user_ids && user_ids?.length > 0) }
    );

    const { data, isFetching, isLoading } =
        user_ids && user_ids.length > 0
            ? { data: accountsByID, isFetching: isAccountsByIDFetching, isLoading: isAccountsByIDLoading }
            : { data: accounts, isFetching: areAccountsFetching, isLoading: areAccountsLoading };

    const [createMailchimpAudienceByIDs, { isLoading: isCreatingByIDs }] = useCreateMailchimpAudienceByIDsMutation();
    const [createMailchimpAudienceByFilter, { isLoading: isCreatingByFilter }] =
        useCreateMailchimpAudienceByFilterMutation();

    const isCreatingMailchimpAudience = isCreatingByIDs || isCreatingByFilter;

    const handleSortClick = (column: string) => {
        const currentColumn = ordering?.replace(/^-/, '');
        const nextSort = currentColumn !== column ? column : ordering?.startsWith('-') ? undefined : `-${column}`;

        setOrdering(nextSort);
    };

    const renderRow = (member: UserList) => (
        <>
            <BorderlessTableCell>
                <Tooltip
                    title={`${
                        member.first_name && member.last_name
                            ? `${member.first_name} ${member.last_name}`
                            : member.full_name
                    }, ${getRoleName(member.role)}`}
                    arrow
                >
                    <Grid2 container alignItems="center">
                        <Grid2 size={2}>
                            <Flag flag={member.country} className="rounded-lg" />
                        </Grid2>
                        <Grid2 size={10}>
                            <Typography
                                color="neutral-800"
                                fontWeight={500}
                                fontSize={14}
                                minHeight="1.5em"
                                className="line-clamp-2 text-ellipsis"
                            >
                                {member.first_name && member.last_name
                                    ? `${member.first_name} ${member.last_name}`
                                    : member.full_name}
                            </Typography>
                        </Grid2>
                        <Grid2 size={10} offset={2}>
                            <Typography color="neutral-600" fontSize={14}>
                                {getRoleName(member.role)}
                            </Typography>
                        </Grid2>
                    </Grid2>
                </Tooltip>
            </BorderlessTableCell>
            <BorderlessTableCell>
                <Tooltip title={member.company_name ? `${member.company_name}, ${member.email}` : member.email} arrow>
                    <span>
                        {member.company_name && (
                            <Typography
                                color="neutral-800"
                                fontSize={14}
                                fontWeight={500}
                                className="text-ellipsis line-clamp-1"
                            >
                                {member.company_name}
                            </Typography>
                        )}
                        <Typography
                            color="neutral-600"
                            fontSize={14}
                            fontWeight={400}
                            className="text-ellipsis line-clamp-1"
                        >
                            {member.email}
                        </Typography>
                    </span>
                </Tooltip>
            </BorderlessTableCell>
        </>
    );

    const handleCreateMailchimpAudience = async () => {
        try {
            if (!audienceName) return;

            if (user_ids && user_ids.length > 0) {
                await createMailchimpAudienceByIDs({ user_ids, audience_name: audienceName }).unwrap();
            } else {
                await createMailchimpAudienceByFilter({
                    params: {
                        search: search ?? '',
                        role: filters?.role ? (filters.role as Roles[]) : undefined,
                        sales_manager: filters?.sales_manager?.map(Number) ?? undefined,
                        country: filters?.country ?? undefined,
                    },
                    audience_name: audienceName,
                }).unwrap();
            }
            toaster.success({ title: 'Success', msg: 'Mailchimp audience created successfully.' });
            handleClose();
        } catch (err) {
            handleError('Failed to create Mailchimp audience.', err);
        }
    };

    const handleClose = () => {
        props.onClose?.({}, 'backdropClick');
        setAudienceName('');
        setPage(1);
        setRowsPerPage(ROWS_PER_PAGE_OPTIONS[0]);
    };

    return (
        <Dialog {...props} onClose={handleClose} classes={{ paper: 'max-w-[830px] w-[830px] dialog-paper py-5 px-10' }}>
            <CloseButton onClick={handleClose} />
            <DialogTitle fontFamily="museo-sans" fontSize="32px !important" letterSpacing={-0.8} textAlign="center">
                Create Mailchimp Audience
                <Typography fontSize={16} letterSpacing={0} pt="16px">
                    The current list of members will be used to create a mailchimp audience.
                </Typography>
                <Box pt="42px">
                    <InputField
                        required
                        label="Audience Name"
                        placeholder="Enter the name of the mailchimp audience"
                        fullWidth
                        helperText="Enter a audience name e.g. Installers Europe."
                        value={audienceName}
                        onChange={(e) => setAudienceName(e.target.value)}
                    />
                </Box>
            </DialogTitle>
            <DialogContent>
                <Table
                    fillMissingRows={false}
                    scrollOnPageChange
                    pagination
                    headers={createMailchimpAudienceDialogHeaders}
                    data={data?.results ?? []}
                    renderRow={renderRow}
                    containerClassname="overflow-visible max-h-[450px]"
                    onPageChange={(newPage) => setPage(newPage)}
                    count={data?.count ?? 0}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[
                        ...ROWS_PER_PAGE_OPTIONS.map((option) => ({ label: option.toString(), value: option })),
                    ]}
                    page={page}
                    setRowsPerPage={setRowsPerPage}
                    isLoading={isFetching || isLoading}
                    onSortClick={handleSortClick}
                    sort={ordering}
                    bottomContainerProps={{
                        className: 'mt-2',
                    }}
                    footerPortalRef={footerPortalRef}
                />
            </DialogContent>
            <DialogActions className="block text-end">
                <div ref={footerPortalRef} className="w-full" />
                <Button
                    fullRounded
                    className="mt-[24px]"
                    disabled={!audienceName}
                    onClick={handleCreateMailchimpAudience}
                    loading={isCreatingMailchimpAudience}
                    loadingPosition="start"
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};
